import React, { useState, useEffect } from "react";
import Label from "../fields/Label";
import { selectClass } from "../fields/fieldStyles";
import { MdClose } from "react-icons/md";
import pica from "pica";
import {
  Post,
  CreatePostInput,
  UpdatePostInput,
  PostAttachment,
  PostType,
} from "../../types/community";
import { useChallenges } from "../../hooks/useChallenges";
import Button from "../common/Button";
import ModalShell from "../modals/ModalShell";
import { logger } from "../../utils/logger";

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024; // Hard limit after compression
const MAX_IMAGE_DIMENSION = 1920;

type PendingAttachment = {
  file: File;
  preview: string;
};

interface PostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePostInput | UpdatePostInput) => Promise<void>;
  post?: Post | null;
  loading?: boolean;
  /** Request error from the owning page; shown in the modal footer banner. */
  error?: string | null;
}

const PostModal: React.FC<PostModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  post = null,
  loading = false,
  error = null,
}) => {
  const [content, setContent] = useState("");
  const [postType, setPostType] = useState<PostType>("general");
  const [challengeId, setChallengeId] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [existingAttachments, setExistingAttachments] = useState<
    PostAttachment[]
  >([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState<number[]>([]);
  const [newAttachments, setNewAttachments] = useState<PendingAttachment[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditMode = !!post;
  const picaInstance = React.useMemo(() => pica(), []);
  const { challenges, loading: challengesLoading } = useChallenges();

  const compressImageIfNeeded = async (file: File): Promise<File> => {
    if (
      !file.type.startsWith("image/") ||
      file.size <= MAX_ATTACHMENT_SIZE_BYTES
    ) {
      return file;
    }

    try {
      const imageBitmap = await createImageBitmap(file);
      const scale =
        imageBitmap.width > imageBitmap.height
          ? Math.min(1, MAX_IMAGE_DIMENSION / imageBitmap.width)
          : Math.min(1, MAX_IMAGE_DIMENSION / imageBitmap.height);

      const targetWidth = Math.max(1, Math.round(imageBitmap.width * scale));
      const targetHeight = Math.max(1, Math.round(imageBitmap.height * scale));

      const sourceCanvas = document.createElement("canvas");
      sourceCanvas.width = imageBitmap.width;
      sourceCanvas.height = imageBitmap.height;
      const sourceCtx = sourceCanvas.getContext("2d");
      sourceCtx?.drawImage(imageBitmap, 0, 0);

      const targetCanvas = document.createElement("canvas");
      targetCanvas.width = targetWidth;
      targetCanvas.height = targetHeight;

      await picaInstance.resize(sourceCanvas, targetCanvas, {
        unsharpAmount: 80,
        unsharpRadius: 0.6,
        unsharpThreshold: 2,
      });
      const blob = await picaInstance.toBlob(targetCanvas, "image/jpeg", 0.82);

      const compressedFile = new File(
        [blob],
        file.name.replace(/\.(png|jpg|jpeg|webp)$/i, "") + ".jpg",
        {
          type: "image/jpeg",
          lastModified: Date.now(),
        }
      );
      return compressedFile.size < file.size ? compressedFile : file;
    } catch (error) {
      logger.warn("Image compression failed, using original file:", error);
      return file;
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && post) {
        setContent(post.content);
        setPostType(post.type || "general");
        setChallengeId(post.challenge_id ?? null);
        setExistingAttachments(post.attachments || []);
      } else {
        setContent("");
        setPostType("general");
        setChallengeId(null);
        setExistingAttachments([]);
      }
      setAttachmentsToRemove([]);
      setNewAttachments((prev) => {
        prev.forEach((attachment) => URL.revokeObjectURL(attachment.preview));
        return [];
      });
      setErrors({});
    } else {
      setNewAttachments((prev) => {
        prev.forEach((attachment) => URL.revokeObjectURL(attachment.preview));
        return [];
      });
    }
  }, [isOpen, isEditMode, post]);

  // Clear challengeId when post type changes away from "challenge"
  useEffect(() => {
    if (postType !== "challenge") {
      setChallengeId(null);
    }
  }, [postType]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!content.trim()) {
      newErrors.content = "Post content is required";
    } else if (content.trim().length < 1) {
      newErrors.content = "Post content cannot be empty";
    } else if (content.length > 5000) {
      newErrors.content = "Post content cannot exceed 5000 characters";
    }

    const totalAttachments = existingAttachments.length + newAttachments.length;
    if (totalAttachments > MAX_ATTACHMENTS) {
      newErrors.attachments = `You can upload up to ${MAX_ATTACHMENTS} attachments`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = event.target.files;
    if (!fileList) {
      return;
    }

    const selectedFiles = Array.from(fileList);
    if (selectedFiles.length === 0) {
      return;
    }

    const remainingSlots =
      MAX_ATTACHMENTS - (existingAttachments.length + newAttachments.length);

    if (remainingSlots <= 0) {
      setErrors((prev) => ({
        ...prev,
        attachments: `Maximum ${MAX_ATTACHMENTS} attachments allowed`,
      }));
      event.target.value = "";
      return;
    }

    const filesToAdd = selectedFiles.slice(0, remainingSlots);

    if (filesToAdd.length < selectedFiles.length) {
      const message =
        remainingSlots === 1
          ? "Only 1 more attachment can be added."
          : `Only ${remainingSlots} more attachments can be added.`;
      setErrors((prev) => ({
        ...prev,
        attachments: message,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        attachments: "",
      }));
    }

    const pendingAttachments: PendingAttachment[] = [];
    for (const file of filesToAdd) {
      const processedFile = await compressImageIfNeeded(file);
      if (processedFile.size > MAX_UPLOAD_SIZE_BYTES) {
        setErrors((prev) => ({
          ...prev,
          attachments: `File "${
            file.name
          }" is too large even after compression. Please choose an image under ${Math.round(
            MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)
          )} MB.`,
        }));
        continue;
      }
      pendingAttachments.push({
        file: processedFile,
        preview: URL.createObjectURL(processedFile),
      });
    }

    if (pendingAttachments.length > 0) {
      setNewAttachments((prev) => [...prev, ...pendingAttachments]);
    }

    event.target.value = "";
  };

  const handleRemoveExistingAttachment = (attachmentId: number) => {
    if (loading) {
      return;
    }

    setExistingAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
    setAttachmentsToRemove((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
    setErrors((prev) => ({
      ...prev,
      attachments: "",
    }));
  };

  const handleRemoveNewAttachment = (index: number) => {
    if (loading) {
      return;
    }

    setNewAttachments((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      const [removed] = updated.splice(index, 1);

      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }

      return updated;
    });

    setErrors((prev) => ({
      ...prev,
      attachments: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const trimmedContent = content.trim();
    const attachmentsToAdd = newAttachments.map(
      (attachment) => attachment.file
    );
    const uniqueAttachmentsToRemove = Array.from(new Set(attachmentsToRemove));

    const data: CreatePostInput | UpdatePostInput = isEditMode
      ? {
          content: trimmedContent,
          attachmentsToAdd,
          attachmentsToRemove: uniqueAttachmentsToRemove,
          // challenge_id: challengeId,
        }
      : {
          content: trimmedContent,
          attachments: attachmentsToAdd,
          type: postType,
          challenge_id: challengeId,
        };

    try {
      setSubmitError(null);
      await onSubmit(data);
    } catch (error) {
      logger.error("Error submitting post:", error);
      const message =
        (error as any)?.message || "Failed to save the post. Please try again.";
      setSubmitError(message);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);

    // Clear content error when user starts typing
    if (errors.content && value.trim()) {
      setErrors((prev) => ({ ...prev, content: "" }));
    }
  };

  if (!isOpen) return null;

  const totalAttachments = existingAttachments.length + newAttachments.length;
  const remainingSlots = Math.max(0, MAX_ATTACHMENTS - totalAttachments);
  const attachmentsDisabled = loading || remainingSlots === 0;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Post" : "Create New Post"}
      size="2xl"
      layout="column"
      closeDisabled={loading}
      message={
        error || submitError
          ? { text: error || submitError, type: "error" }
          : null
      }
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={!content.trim()}>
            {isEditMode ? "Update Post" : "Create Post"}
          </Button>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="content">Post Content *</Label>
              <textarea
                id="content"
                value={content}
                onChange={handleContentChange}
                placeholder="What's on your mind? Share your thoughts with the community..."
                rows={8}
                className={`w-full resize-none rounded-md border px-3 py-2 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-1 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400 ${
                  errors.content
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
                disabled={loading}
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-500">{errors.content}</p>
              )}
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {content.length}/5000 characters
              </div>
            </div>

            {/* Post Type Selector - Only show when creating new posts */}
            {!isEditMode && (
              <div>
                <Label htmlFor="post-type">Post Type *</Label>
                <select
                  id="post-type"
                  value={postType}
                  onChange={(e) => setPostType(e.target.value as PostType)}
                  className={selectClass()}
                  disabled={loading}
                >
                  <option value="general">General</option>
                  <option value="support">Support</option>
                  <option value="challenge">Challenge</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>
            )}

            {/* Related Challenge */}
            {!isEditMode && postType === "challenge" && (
              <div>
                <Label htmlFor="challenge">Related Challenge</Label>
                <select
                  id="challenge"
                  value={challengeId || ""}
                  onChange={(e) =>
                    setChallengeId(
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  className={selectClass()}
                  disabled={loading || challengesLoading}
                >
                  {challengesLoading ? (
                    <option value="">Loading...</option>
                  ) : (
                    <>
                      <option value="">No Challenge</option>
                      {challenges.map((challenge) => (
                        <option key={challenge.id} value={challenge.id}>
                          {challenge.name} ({challenge.conducted_at_year})
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            )}

            <div>
              <Label htmlFor="post-attachments">Attachments</Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Add up to {MAX_ATTACHMENTS} images. Existing attachments can be
                removed below.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  id="post-attachments"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  disabled={attachmentsDisabled}
                  className="hidden"
                />
                <label
                  htmlFor="post-attachments"
                  className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    attachmentsDisabled
                      ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-700"
                      : "cursor-pointer bg-biafit-primary text-white hover:bg-biafit-secondary"
                  }`}
                >
                  Add Images
                </label>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {totalAttachments}/{MAX_ATTACHMENTS} selected •{" "}
                  {remainingSlots > 0
                    ? `${remainingSlots} attachment${
                        remainingSlots === 1 ? "" : "s"
                      } remaining`
                    : "Maximum attachments reached"}
                </span>
              </div>

              {errors.attachments && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.attachments}
                </p>
              )}

              {(existingAttachments.length > 0 ||
                newAttachments.length > 0) && (
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {existingAttachments.map((attachment) => (
                    <div
                      key={`existing-${attachment.id}`}
                      className="relative overflow-hidden rounded-md border border-gray-200 dark:border-gray-700"
                    >
                      <img
                        src={attachment.full_path}
                        alt={attachment.original_name || "Post attachment"}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute left-2 top-2">
                        <span className="rounded bg-white/80 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-900/70 dark:text-gray-200">
                          Existing
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveExistingAttachment(attachment.id)
                        }
                        disabled={loading}
                        className="bg-black/60 hover:bg-black/80 absolute right-2 top-2 rounded-full p-1 text-white transition-colors"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {newAttachments.map((attachment, index) => (
                    <div
                      key={`new-${index}`}
                      className="relative overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/40"
                    >
                      <img
                        src={attachment.preview}
                        alt={attachment.file.name}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute left-2 top-2">
                        <span className="rounded bg-biafit-primary/80 px-2 py-0.5 text-xs font-medium text-white">
                          New
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewAttachment(index)}
                        disabled={loading}
                        className="bg-black/60 hover:bg-black/80 absolute right-2 top-2 rounded-full p-1 text-white transition-colors"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                      <div className="bg-black/50 absolute bottom-0 left-0 right-0">
                        <p className="truncate px-2 py-1 text-xs text-white">
                          {attachment.file.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default PostModal;
